import { useCallback } from 'react';

import { useRouter } from '../router';
import { selectNextQueuedId, selectNowPlayingId, useStore } from '../store';
import { useT } from '../useT';
import { useQueueActions } from './useQueueActions';

export interface StartQueue {
  /** Opens the player on what the room is (or should be) singing. */
  start: () => void;
  /** False when there is nothing to start; `start` then says so instead of doing nothing. */
  canStart: boolean;
}

/**
 * "Start the room's queue", for the controls that are not the queue drawer (UX-360).
 *
 * With three songs waiting and the TV idle on Home, `MediaPlay`, `MediaPlayPause` and
 * `MediaTrackNext` were all inert: no screen change, no focus change, no toast, not even an edge
 * cue. The queue *was* startable — rail → "Fila" → Enter → Enter on the first row — but that is
 * three presses through a drawer the room has to know exists, while the big play button every TV
 * remote has answered with nothing at all.
 *
 * The rules are the ones the drawer already follows: something playing → open the player on it;
 * something queued → `POST play-now` on the head of the queue (which fronts it and navigates);
 * nothing at all → say so, because a key that means nothing is exactly the complaint.
 */
export function useStartQueue(): StartQueue {
  const { t } = useT();
  const navigate = useRouter((s) => s.navigate);
  const showToast = useStore((s) => s.showToast);
  const playingId = useStore(selectNowPlayingId);
  const nextId = useStore(selectNextQueuedId);
  const next = useStore((s) => (nextId ? (s.queue.items[nextId] ?? null) : null));
  const actions = useQueueActions();

  const start = useCallback(() => {
    if (playingId) {
      navigate('player', { itemId: playingId });
      return;
    }
    if (!nextId || !next) {
      showToast(t('tv:queue.empty'));
      return;
    }
    actions.playNow(nextId, next.tone);
  }, [playingId, nextId, next, navigate, showToast, t, actions]);

  return { start, canStart: Boolean(playingId || nextId) };
}
